<template>
  <div class="relative mt-10 mx-3" dir="ltr">
    <div
      class="absolute h-1 w-full rounded"
      :class="disabled ? 'bg-neutral-quinary-disable' : 'bg-neutral-tertiary'"
    >
      <div>
        <Image
          class="absolute left-1/4 border-transparent z-10"
          src="thick"
          alt="thick"
        />
        <Image
          class="absolute left-1/2 border-transparent z-10"
          src="thick"
          alt="thick"
        />
        <Image
          class="absolute left-3/4 border-transparent z-10"
          src="thick"
          alt="thick"
        />
      </div>

      <div
        class="absolute h-1 rounded"
        :class="disabled ? 'bg-neutral-quinary-disable' : colorClass[color]"
        :style="{ width: `${validRange}%` }"
        dir="ltr"
      />

      <div v-if="showRangeLabel" class="absolute w-full -top-7">
        <NText
          variant="caption1"
          weight="bold"
          class="relative -left-2 w-full block"
          :class="
            disabled ? 'text-neutral-primary-disable' : 'text-neutral-primary'
          "
          :style="
            validRange < 97
              ? { transform: `translateX(${validRange}%)` }
              : { textAlign: 'right', left: '0.5rem' }
          "
        >
          {{ range }}%
        </NText>
      </div>
      <div
        class="relative w-full z-10"
        :style="{ transform: `translateX(${validRange}%)` }"
      >
        <div
          class="absolute -top-2 -left-2 w-5 h-5 rounded-full z-20 cursor-pointer"
          :class="disabled ? 'bg-neutral-quinary-disable' : colorClass[color]"
        />
        <div
          class="absolute -top-0.5 -left-0.5 w-2 h-2 rounded-full z-30 cursor-pointer"
          :class="
            disabled ? 'bg-neutral-quinary-disable' : 'bg-neutral-primary'
          "
        />
      </div>
    </div>

    <input
      type="range"
      class="w-full h-1.5 z-20 opacity-0 absolute"
      :class="disabled ? 'cursor-default' : 'cursor-pointer'"
      :value="range"
      min="0"
      max="100"
      dir="ltr"
      :step="step"
      :disabled="disabled"
      @input.prevent="changeRangeValue"
    />
  </div>

  <div
    v-if="showThickLabels"
    class="relative mx-3 mt-14 mb-3 h-4"
    :class="
      disabled ? 'text-neutral-primary-disable' : 'text-neutral-secondary'
    "
    dir="ltr"
  >
    <!-- eslint-disable -->
    <NText variant="caption1" class="absolute left-[0%]"> 0% </NText>
    <NText variant="caption1" class="absolute left-[23%]"> 25% </NText>
    <NText variant="caption1" class="absolute left-[48%]"> 50% </NText>
    <NText variant="caption1" class="absolute left-[73%]"> 75% </NText>
    <NText variant="caption1" class="absolute left-[93%]"> 100% </NText>
    <!-- eslint-enable -->
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useDependentRef } from '@libs/use';

import Image from '@/components/Image.vue';

type SliderProps = {
  modelValue?: number | string;
  showThickLabels?: boolean;
  showRangeLabel?: boolean;
  maxValue?: number;
  color?: 'red' | 'green';
  step?: number;
  disabled?: boolean;
};
type SliderEmits = {
  (e: 'change', value: number): void;
};

const props = withDefaults(defineProps<SliderProps>(), {
  modelValue: 0,
  showThickLabels: true,
  showRangeLabel: true,
  maxValue: 100,
  color: 'green',
  step: 5,
  disabled: false,
});
const emit = defineEmits<SliderEmits>();

const mapPercentToValue = useDebounceFn((percent: string | number) => {
  const value = Math.floor((props.maxValue * Number(percent)) / 100);

  emit('change', value);
}, 10);

const mapValueToPercent = (value: number | string) => {
  return Math.round((Number(value) * 100) / props.maxValue);
};

const range = useDependentRef(toRef(props, 'modelValue'), 0, mapValueToPercent);

const changeRangeValue = (event: Event) => {
  if (event.target) {
    range.value = (event.target as HTMLInputElement).value;
  }

  mapPercentToValue(range.value);
};

const validRange = computed(() => {
  if (range.value > 100) {
    return 100;
  }

  if (range.value < 0) {
    return 0;
  }

  return range.value;
});

const colorClass = {
  red: 'bg-critical-primary',
  green: 'bg-brand-primary',
} as const;
</script>
